import { useAppDataStore } from "@/mobx/appDataStore.jsx";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import formatters from "helpers/formatters.jsx";

export default observer(function Products(props) {
  const appDataStore = useAppDataStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);

  const pageSize = 20;

  // Cargar productos al montar el componente
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async (page = 0, search = "", category = "") => {
    try {
      setLoading(true);
      const data = await appDataStore.post("/products/list", {
        limit: pageSize,
        offset: page * pageSize,
        search: search || searchTerm,
        category: category || selectedCategory
      });
      setProducts(data.products || []);
      setTotalProducts(data.total || 0);
      setCategories(data.categories || []);
      setCurrentPage(page);
    } catch (err) {
      console.error("Error al cargar productos:", err);
      alert("Error al cargar productos: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(0);
    loadProducts(0, value, selectedCategory);
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setSelectedCategory(value);
    setCurrentPage(0);
    loadProducts(0, searchTerm, value);
  };

  const getEnabledBadgeClass = (enabled) => {
    return enabled ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
  };

  const totalPages = Math.ceil(totalProducts / pageSize);

  return (
    <>
      <div className="text-black">
        {/* Filtros */}
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar (nombre, descripción, categoría)
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Ej: Disney Plus"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoría
              </label>
              <select
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">Todas</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tabla de Productos */}
        {loading && products.length === 0 ? (
          <div className="text-center py-8 text-gray-600">Cargando productos...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-8 text-gray-600">No hay productos</div>
        ) : (
          <>
            <div className="overflow-x-auto border border-gray-300 rounded-lg">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-300">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">ID</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nombre</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Categoría</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Descripción</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Precio</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">País</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="border-b border-gray-300 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">
                        {product.id}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-800">
                        {product.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        {product.category}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {product.description ? product.description.substring(0, 40) + (product.description.length > 40 ? "..." : "") : "-"}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">
                        {product.price ? formatters.cash(product.price) : "0.00"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        {product.country || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getEnabledBadgeClass(product.enabled)}`}>
                          {product.enabled ? "Habilitado" : "Deshabilitado"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  onClick={() => loadProducts(Math.max(0, currentPage - 1), searchTerm, selectedCategory)}
                  disabled={currentPage === 0 || loading}
                  className="px-4 py-2 border border-gray-300 rounded bg-white hover:bg-gray-100 disabled:bg-gray-200 text-gray-800"
                >
                  Anterior
                </button>

                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => loadProducts(i, searchTerm, selectedCategory)}
                      className={`px-3 py-2 rounded text-sm font-medium ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-white border border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => loadProducts(Math.min(totalPages - 1, currentPage + 1), searchTerm, selectedCategory)}
                  disabled={currentPage === totalPages - 1 || loading}
                  className="px-4 py-2 border border-gray-300 rounded bg-white hover:bg-gray-100 disabled:bg-gray-200 text-gray-800"
                >
                  Siguiente
                </button>

                <span className="text-sm text-gray-600 ml-4">
                  Página {currentPage + 1} de {totalPages} ({totalProducts} productos)
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
});
